import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Download, Star, Trash2, Edit, Tag, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ResumeUploader } from '@/components/resume/ResumeUploader';

interface Resume {
  id: string;
  source_type: string;
  optimization_level: string | null;
  ats_score_after: number | null;
  created_at: string;
  is_default: boolean;
  version_name: string | null;
  tags: string[] | null;
  pdf_url: string | null;
  optimized_content: any;
}

export default function ResumeLibrary() {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'upload' | 'profile' | 'tailored'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [editingResume, setEditingResume] = useState<Resume | null>(null);
  const [newName, setNewName] = useState('');
  const [newTags, setNewTags] = useState('');
  const [showOptimizeDialog, setShowOptimizeDialog] = useState(false);
  const [uploadedResumeId, setUploadedResumeId] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadResumes();
  }, []);

  const loadResumes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('optimized_resumes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResumes((data || []) as Resume[]);
    } catch (error) {
      console.error('Error loading resumes:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load resumes',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = async () => {
    console.log('Resume uploaded, refreshing list...');
    await loadResumes();
  };

  const handleOptimize = async (level: 'light' | 'moderate' | 'aggressive') => {
    if (!uploadedResumeId) return;
    setShowOptimizeDialog(false);
    navigate(`/app/resume/optimize?resumeId=${uploadedResumeId}&level=${level}`);
  };

  const handleSetDefault = async (resumeId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First, unset all defaults
      await supabase
        .from('optimized_resumes')
        .update({ is_default: false })
        .eq('user_id', user.id);

      // Then set the new default
      const { error } = await supabase
        .from('optimized_resumes')
        .update({ is_default: true })
        .eq('id', resumeId);

      if (error) throw error;

      toast({
        title: 'Default resume updated',
        description: 'This resume will be used automatically in Smart Apply',
      });

      loadResumes();
    } catch (error) {
      console.error('Error setting default:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to set default resume',
      });
    }
  };

  const handleDownload = async (resume: Resume) => {
    try {
      if (resume.pdf_url) {
        window.open(resume.pdf_url, '_blank');
        return;
      }

      toast({
        title: 'Generating PDF',
        description: 'Please wait...',
      });

      const { data, error } = await supabase.functions.invoke('generate-resume-pdf', {
        body: { resumeId: resume.id }
      });

      if (error) throw error;

      window.open(data.url, '_blank');

      toast({
        title: 'PDF generated',
        description: 'Your resume is ready to download',
      });

      loadResumes();
    } catch (error) {
      console.error('Error downloading:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to generate PDF',
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from('optimized_resumes')
        .delete()
        .eq('id', deleteId);

      if (error) throw error;

      toast({
        title: 'Resume deleted',
        description: 'Your resume has been removed',
      });

      setResumes(resumes.filter(r => r.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete resume',
      });
    }
  };

  const handleRename = (resume: Resume) => {
    setEditingResume(resume);
    setNewName(resume.version_name || '');
    setRenameDialogOpen(true);
  };

  const handleSaveRename = async () => {
    if (!editingResume || !newName.trim()) return;

    try {
      const { error } = await supabase
        .from('optimized_resumes')
        .update({ version_name: newName.trim() })
        .eq('id', editingResume.id);

      if (error) throw error;

      toast({
        title: 'Resume renamed',
        description: 'Your resume has been renamed successfully',
      });

      loadResumes();
      setRenameDialogOpen(false);
    } catch (error) {
      console.error('Error renaming:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to rename resume',
      });
    }
  };

  const handleEditTags = (resume: Resume) => {
    setEditingResume(resume);
    setNewTags(resume.tags?.join(', ') || '');
    setTagsDialogOpen(true);
  };

  const handleSaveTags = async () => {
    if (!editingResume) return;

    const tags = newTags
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    try {
      const { error } = await supabase
        .from('optimized_resumes')
        .update({ tags })
        .eq('id', editingResume.id);

      if (error) throw error;

      toast({
        title: 'Tags updated',
        description: 'Your resume tags have been updated',
      });

      loadResumes();
      setTagsDialogOpen(false);
    } catch (error) {
      console.error('Error updating tags:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update tags',
      });
    }
  };

  const filteredResumes = resumes.filter(resume => {
    if (filter !== 'all' && resume.source_type !== filter) return false;
    if (searchTerm && !resume.version_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Resume Library | LoopBaan.AI</title>
      </Helmet>

      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <FileText className="h-8 w-8" />
              Resume Library
            </h1>
            <p className="text-muted-foreground mt-2">
              Upload and manage your optimized resumes
            </p>
          </div>
        </div>

        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle>Upload & Optimize Existing Resume</CardTitle>
          </CardHeader>
          <CardContent>
            <ResumeUploader onUploadComplete={handleUploadComplete} />
          </CardContent>
        </Card>

        {/* Create From Scratch Section */}
        <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Don't have a resume yet?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Let our AI create a professional, ATS-optimized resume from scratch using your profile information.
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">✓</span>
                <span>Professional templates for any industry</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">✓</span>
                <span>AI-generated content with target keywords</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">✓</span>
                <span>ATS-friendly from the start</span>
              </li>
            </ul>
            <Button 
              onClick={() => navigate('/app/resume')}
              className="w-full"
              size="lg"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Create Resume with AI
            </Button>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Input
            placeholder="Search resumes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
          <Select value={filter} onValueChange={(value: any) => setFilter(value)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Resumes</SelectItem>
              <SelectItem value="upload">Uploaded</SelectItem>
              <SelectItem value="profile">From Profile</SelectItem>
              <SelectItem value="tailored">Tailored</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Resumes Grid */}
        {filteredResumes.length === 0 ? (
          <Card className="p-12">
            <div className="text-center space-y-4">
              <FileText className="h-16 w-16 mx-auto text-muted-foreground" />
              <div>
                <h3 className="text-xl font-semibold mb-2">No resumes yet</h3>
                <p className="text-muted-foreground mb-4">
                  Upload your first resume to get started
                </p>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredResumes.map((resume) => (
              <Card key={resume.id} className="relative">
                {resume.is_default && (
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-yellow-500/20 text-yellow-500">
                      <Star className="h-3 w-3 mr-1 fill-current" />
                      Default
                    </Badge>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-lg flex items-start justify-between">
                    <span className="pr-16">
                      {resume.version_name || `Resume ${resume.id.substring(0, 8)}`}
                    </span>
                  </CardTitle>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge variant="outline">
                      {resume.source_type}
                    </Badge>
                    {resume.optimization_level && (
                      <Badge variant="secondary">
                        {resume.optimization_level}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {resume.ats_score_after && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">ATS Score:</span>
                      <Badge variant="outline" className="text-lg">
                        {resume.ats_score_after}/100
                      </Badge>
                    </div>
                  )}

                  <div className="text-sm text-muted-foreground">
                    Created: {new Date(resume.created_at).toLocaleDateString()}
                  </div>

                  {resume.tags && resume.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {resume.tags.map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(resume)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                    {!resume.is_default && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSetDefault(resume.id)}
                      >
                        <Star className="h-4 w-4 mr-1" />
                        Set Default
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRename(resume)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Rename
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditTags(resume)}
                    >
                      <Tag className="h-4 w-4 mr-1" />
                      Tags
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeleteId(resume.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Optimize Dialog */}
      <Dialog open={showOptimizeDialog} onOpenChange={setShowOptimizeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Optimize Your Resume?</DialogTitle>
            <DialogDescription>
              Choose how aggressively you'd like to optimize this resume for ATS compatibility.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button 
              onClick={() => handleOptimize('light')}
              className="w-full justify-start"
              variant="outline"
            >
              <div className="text-left">
                <div className="font-semibold">Light Optimization (1 credit)</div>
                <div className="text-xs text-muted-foreground">Minor improvements, preserve original style</div>
              </div>
            </Button>
            <Button 
              onClick={() => handleOptimize('moderate')}
              className="w-full justify-start"
              variant="outline"
            >
              <div className="text-left">
                <div className="font-semibold">Moderate Optimization (1 credit)</div>
                <div className="text-xs text-muted-foreground">Rewrite bullets, add keywords</div>
              </div>
            </Button>
            <Button 
              onClick={() => handleOptimize('aggressive')}
              className="w-full justify-start"
            >
              <div className="text-left">
                <div className="font-semibold">Aggressive Optimization (2 credits) - Recommended</div>
                <div className="text-xs text-muted-foreground">Complete rewrite for maximum ATS score</div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => setShowOptimizeDialog(false)}
              variant="ghost"
            >
              Skip for Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Resume?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your resume.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Resume</DialogTitle>
            <DialogDescription>
              Give your resume a memorable name
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Resume Name</Label>
              <Input
                id="name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Software Engineer - Google"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRename}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tags Dialog */}
      <Dialog open={tagsDialogOpen} onOpenChange={setTagsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tags</DialogTitle>
            <DialogDescription>
              Add tags to organize your resumes (comma-separated)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tags">Tags</Label>
              <Input
                id="tags"
                value={newTags}
                onChange={(e) => setNewTags(e.target.value)}
                placeholder="e.g., Software Engineer, Product Manager, Tech"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTags}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
